/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useBoolean, useDebounceValue, usePositioner, error, safeInvoke, useMergedRef } from '@sravni/react-utils';
import { Cross } from '@sravni/react-icons';
import { Z_INDEXES } from '../../../constants/zIndexes.js';
import '../../Portal/index.js';
import '../../Transition/index.js';
import '../../Space/index.js';
import '../../Typography/index.js';
import '../../Icon/index.js';
import modules_39642e22 from '../styles/Tooltip.module.less.js';
import Portal from '../../Portal/src/Portal.js';
import { Fade } from '../../Transition/src/Fade.js';
import Space from '../../Space/src/Space.js';
import { Text } from '../../Typography/src/Text.js';
import Icon from '../../Icon/src/Icon.js';

/**
 * @description The Tooltip component is used to show more content of a target
 *
 * @component
 * @example
 * ```jsx
 * <Tooltip title="prompt text">
 *   <span>Tooltip will show on mouse enter or by taps on mobile.</span>
 * </Tooltip>
 * ```
 */
var Tooltip = React.forwardRef(function (props, ref) {
    var _a;
    var title = props.title, content = props.content, footer = props.footer, _b = props.triggerMode, triggerMode = _b === void 0 ? 'hover' : _b, closable = props.closable, placement = props.placement, width = props.width, className = props.className, style = props.style, children = props.children, onClose = props.onClose, restProps = __rest(props, ["title", "content", "footer", "triggerMode", "closable", "placement", "width", "className", "style", "children", "onClose"]);
    var cx = useStyles(modules_39642e22);
    var reference = React.useRef(null);
    var popper = React.useRef(null);
    var _c = __read(useBoolean(false), 2), visible = _c[0], setVisible = _c[1];
    var visibleDebounce = useDebounceValue(visible, 300);
    var _d = usePositioner({
        placement: placement,
        offset: [0, 16],
        modifiers: [
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['top', 'right', 'bottom', 'left'],
                },
            },
        ],
    }), referenceRef = _d.referenceRef, popperRef = _d.popperRef, arrowRef = _d.arrowRef, getReferenceProps = _d.getReferenceProps, getPopperProps = _d.getPopperProps;
    var elementStyles = React.useMemo(function () {
        var styles = __assign({}, style);
        styles.width = width;
        return styles;
    }, [style, width]);
    var triggerElement;
    // Ensure tooltip has only one child node
    var count = React.useMemo(function () { return React.Children.count(children); }, [children]);
    var condition = count === 0 || count > 1;
    if (condition) {
        error({
            condition: condition,
            message: 'Ensure tooltip has only one child node',
        })();
        triggerElement = children;
    }
    else {
        var child_1 = React.Children.only(children);
        var handleMouseEnter = function (event) {
            setVisible.on();
            safeInvoke(child_1.props.onMouseEnter, event);
        };
        var handleMouseLeave = function (event) {
            setVisible.off();
            safeInvoke(child_1.props.onMouseLeave, event);
        };
        var handleClick = function (event) {
            setVisible.toggle();
            safeInvoke(child_1.props.onClick, event);
        };
        var handleTouchEnd = function (event) {
            setVisible.toggle();
            safeInvoke(child_1.props.onTouchEnd, event);
        };
        var triggerProps = __assign(__assign(__assign({}, child_1.props), getReferenceProps({}, reference)), { tabIndex: (_a = child_1.props.tabIndex) !== null && _a !== void 0 ? _a : 0, className: cx('outlineNone', child_1.props.className), ref: useMergedRef(referenceRef, child_1.ref) });
        if (triggerMode === 'click') {
            triggerProps.onClick = handleClick;
        }
        else {
            triggerProps.onMouseEnter = handleMouseEnter;
            triggerProps.onMouseLeave = handleMouseLeave;
        }
        triggerProps.onTouchEnd = handleTouchEnd;
        triggerElement = React.cloneElement(child_1, triggerProps);
    }
    var handleClose = React.useCallback(function (event) {
        setVisible.off();
        safeInvoke(onClose, event);
    }, [onClose]);
    return (React.createElement(React.Fragment, null,
        triggerElement,
        React.createElement(Portal, null,
            React.createElement(Fade, __assign({ ref: popperRef, className: cx('bubble'), visible: visibleDebounce, unmountOnExit: true }, getPopperProps({ style: { zIndex: Z_INDEXES.TOOLTIP } }, popper)),
                React.createElement("div", { ref: arrowRef, className: cx('arrow') }),
                React.createElement("div", __assign({}, restProps, { "data-qa": "Tooltip", ref: ref, className: cx(className, 'container'), style: elementStyles, onMouseEnter: setVisible.on, onMouseLeave: function () { return triggerMode === 'hover' && setVisible.off(); } }),
                    React.createElement(Space, { direction: "vertical", size: 8 },
                        title && (React.createElement(Space, { justify: "space-between", size: 8 },
                            React.createElement(Text, { size: 16, strong: true }, title),
                            closable && React.createElement(Icon, { className: cx('close'), icon: React.createElement(Cross, null), size: 16, onClick: handleClose }))),
                        content && React.createElement(Text, { size: 14 }, content),
                        footer && footer))))));
});
Tooltip.defaultProps = {
    placement: 'top',
    width: 300,
    triggerMode: 'hover',
};
Tooltip.displayName = 'Tooltip';

export default Tooltip;
//# sourceMappingURL=Tooltip.js.map
